// @flow

import { isObject } from 'lodash';

import { Spacing as ThemeSpacing } from '@zapier/theme';

export type SpacingAmount = string | number | boolean;

export const spacingTypes = Object.freeze({
  all: 'all',
  bottom: 'bottom',
  horizontal: 'horizontal',
  left: 'left',
  right: 'right',
  top: 'top',
  vertical: 'vertical',
});

export type SpacingType = $Keys<typeof spacingTypes>;

export type SpacingProp = 'margin' | 'padding';

export type SpacingObj = {
  [SpacingType]: SpacingAmount,
};

export type Spacing = SpacingAmount | SpacingObj;

/**
 * Dimensional Tests
 * key: dimension
 * value: tests array in priority order
 */
const dimensions: { [string]: Array<string> } = {
  Bottom: [spacingTypes.bottom, spacingTypes.vertical, spacingTypes.all],
  Left: [spacingTypes.left, spacingTypes.horizontal, spacingTypes.all],
  Right: [spacingTypes.right, spacingTypes.horizontal, spacingTypes.all],
  Top: [spacingTypes.top, spacingTypes.vertical, spacingTypes.all],
};

const resolveSpacing = (
  value: boolean | $Keys<typeof ThemeSpacing>,
  defaultAmount: $Keys<typeof ThemeSpacing>
) => {
  // if true we want to use defaultAmount as the value
  if (value === true) return ThemeSpacing[defaultAmount] || defaultAmount || 0;

  // If false always use 0 as value (using it as a clear)
  if (value === false) return 0;

  // Try to find a theme spacing
  return ThemeSpacing[value] || value || defaultAmount || 0;
};

const convertSpacings = (
  spacingDef: SpacingObj,
  defaultAmount: ?string,
  prop: SpacingProp
) => {
  const spacings = Object.keys(spacingDef);
  return Object.keys(dimensions).reduce((acc, dim) => {
    // look for any spacings that match the tests for the dimension
    const spacing = dimensions[dim].find(test => spacings.includes(test));
    if (spacing) {
      // we found a matching spacing so apply it to the return
      return {
        ...acc,
        // $FlowFixMe
        [`${prop}${dim}`]: resolveSpacing(spacingDef[spacing], defaultAmount),
      };
    }
    // no matching spacing was found so don't add anything
    return acc;
  }, {});
};

/**
 * Converts spacing definitions to css-in-js spacings.
 */
/** @component */
export const spacingToCss = (prop: SpacingProp) => (
  spacingDef: ?Spacing,
  defaultAmount?: string = '20px'
) => {
  // Just return empty object if the spacing def wasn't supplied
  if (spacingDef === null || spacingDef === undefined) return {};

  if (isObject(spacingDef)) {
    // spacingDef is an object so we can just pass it through
    return convertSpacings(
      ((spacingDef: any): SpacingObj),
      defaultAmount,
      prop
    );
  }

  // spacing def is a string / number / boolean
  if (spacingTypes[((spacingDef: any): string)]) {
    // spacingDef is one of the spacingTypes so use defaultAmount with it
    return convertSpacings(
      { [((spacingDef: any): string)]: true },
      defaultAmount,
      prop
    );
  } else {
    // spacingDef is an unknown string / number / boolean so use it with 'all'
    return convertSpacings(
      { [spacingTypes.all]: ((spacingDef: any): SpacingAmount) },
      defaultAmount,
      prop
    );
  }
};

export const marginToCss = spacingToCss('margin');
export const paddingToCss = spacingToCss('padding');
